/**
 * Server-side Permission Utilities
 * 
 * Simple permission checking functions for API routes
 */

import type { User } from '@/lib/types';

/**
 * Check if user is Super Admin
 */
export const isSuperAdmin = (user: User | null | undefined): boolean => {
  return user?.roles?.includes('Super Admin') || false;
};

/**
 * Check if user is Admin
 */
export const isAdmin = (user: User | null | undefined): boolean => {
  return user?.roles?.includes('Admin') || false;
};

/**
 * Check if user is Staff level (Staff, Tennis Club, Teacher, etc.)
 */
export const isStaff = (user: User | null | undefined): boolean => {
  return user?.roles?.includes('Staff') || 
         user?.roles?.includes('Tennis Club') || 
         user?.roles?.includes('Teacher') || 
         user?.roles?.includes('Tennis School') || false;
};

/**
 * Check if user can manage a specific club
 */
export const canManageClub = (user: User | null | undefined, clubId: string): boolean => {
  // Super Admin can manage all clubs
  if (isSuperAdmin(user)) return true;
  
  // Admin can manage all clubs in their organization
  if (isAdmin(user)) return true;
  
  // Others can only manage clubs they're associated with
  return user?.associatedClubIds?.includes(clubId) || false;
};

/**
 * Check if user can manage spaces for a specific club
 */
export const canManageSpacesForClub = (user: User | null | undefined, clubId: string): boolean => {
  return canManageClub(user, clubId);
};

/**
 * Check if user has admin-level access (Admin or Super Admin)
 */
export const hasAdminAccess = (user: User | null | undefined): boolean => {
  return isAdmin(user) || isSuperAdmin(user);
};

/**
 * Check if user has staff-level access (Staff, Admin, or Super Admin)
 */
export const hasStaffAccess = (user: User | null | undefined): boolean => {
  return isStaff(user) || isAdmin(user) || isSuperAdmin(user);
};

/**
 * Get organization filter for queries (null for Super Admin, organizationId for others)
 */
export const getOrganizationFilter = (user: User | null | undefined): string | null => {
  return isSuperAdmin(user) ? null : user?.organizationId || null;
}; 
